import { Helmet } from "react-helmet-async";
import LegalLayout from "@/components/legal/LegalLayout";

const sections = [
  { id: "scope", title: "Scope" },
  { id: "collect", title: "Information We Collect" },
  { id: "use", title: "How We Use Information" },
  { id: "legal-bases", title: "Legal Bases" },
  { id: "sharing", title: "How We Share" },
  { id: "ai", title: "AI Features" },
  { id: "retention", title: "Retention" },
  { id: "security", title: "Security" },
  { id: "rights", title: "Your Rights" },
  { id: "children", title: "Children" },
  { id: "international", title: "International Users" },
  { id: "changes", title: "Changes" },
  { id: "contact", title: "Contact" },
];

const PrivacyPolicy = () => (
  <>
    <Helmet>
      <title>Privacy Policy — The Emerging Market</title>
      <meta name="description" content="How The Emerging Market collects, uses, shares, and protects your personal information and the ideas you submit." />
      <link rel="canonical" href="https://emrkt.co/privacy" />
    </Helmet>
    <LegalLayout
      title="Privacy Policy"
      description="How we handle your personal information and the ideas, projects, and content you share with The Emerging Market."
      lastUpdated="June 5, 2026"
      effectiveDate="June 5, 2026"
      sections={sections}
    >
      <section id="scope">
        <h2>1. Scope</h2>
        <p>
          This Privacy Policy describes how The Emerging Market ("TEM," "we," "us") collects, uses, and shares
          information when you visit <a href="https://emrkt.co">emrkt.co</a>, create an account, submit ideas or
          projects, use our AI features, or otherwise interact with our services (collectively, the "Service").
        </p>
        <p>
          By using the Service you agree to this Policy and our{" "}
          <a href="/terms">Terms of Service</a>. If you do not agree, please do not use the Service.
        </p>
      </section>

      <section id="collect">
        <h2>2. Information We Collect</h2>
        <h3>2.1 Information you provide</h3>
        <ul>
          <li><strong>Account information:</strong> name, email, password (hashed), role, organization, profile photo, bio.</li>
          <li><strong>Profile information:</strong> location, focus areas, interests, credentials, social/LinkedIn links.</li>
          <li><strong>Ideas and projects:</strong> titles, descriptions, attachments, visibility settings (public/private), updates, asks, inbox messages, and AI coach conversations associated with your projects.</li>
          <li><strong>Communications:</strong> messages you send to us, support requests, signup form submissions, sign-up dialog responses.</li>
          <li><strong>Affiliate / partner submissions:</strong> business name, services, location, and supporting materials when you apply.</li>
        </ul>
        <h3>2.2 Information collected automatically</h3>
        <ul>
          <li><strong>Usage data:</strong> pages viewed, features used, referring URL, timestamps, approximate device and browser type.</li>
          <li><strong>Cookies and similar technologies:</strong> see our <a href="/cookies">Cookie Policy</a>.</li>
          <li><strong>Email engagement:</strong> opens, clicks, bounces, unsubscribes for transactional and notification emails.</li>
          <li><strong>Performance and error data:</strong> Core Web Vitals and crash reports captured via Sentry (does not include the content of your ideas).</li>
        </ul>
        <h3>2.3 Information from third parties</h3>
        <ul>
          <li><strong>Authentication providers</strong> (e.g., Google, LinkedIn) — basic profile fields you authorize.</li>
          <li><strong>Public data sources</strong> we surface in the Service (e.g., U.S. Census ACS, Alabama legislation feeds, news RSS). This is not your personal information.</li>
        </ul>
      </section>

      <section id="use">
        <h2>3. How We Use Information</h2>
        <ul>
          <li>Operate, maintain, and improve the Service, including matching your ideas with grants, evidence, and partners.</li>
          <li>Authenticate you, secure your account, and enforce our <a href="/acceptable-use">Acceptable Use Policy</a>.</li>
          <li>Send transactional emails (account, security, deadlines you opted into).</li>
          <li>Provide AI coaching, grant fit analysis, scoring, and recommendations on content you submit.</li>
          <li>Generate aggregated, de-identified analytics to understand product usage. Aggregated data cannot reasonably be used to identify you.</li>
          <li>Comply with legal obligations and respond to lawful requests.</li>
        </ul>
      </section>

      <section id="legal-bases">
        <h2>4. Legal Bases (where applicable)</h2>
        <p>Where the EU/UK GDPR or similar laws apply, we rely on:</p>
        <ul>
          <li><strong>Contract:</strong> to deliver the Service you signed up for.</li>
          <li><strong>Legitimate interests:</strong> to secure the Service, prevent abuse, and improve features.</li>
          <li><strong>Consent:</strong> for optional communications and certain cookies. You can withdraw consent at any time.</li>
          <li><strong>Legal obligation:</strong> tax, accounting, and law-enforcement compliance.</li>
        </ul>
      </section>

      <section id="sharing">
        <h2>5. How We Share Information</h2>
        <p>We do <strong>not sell</strong> your personal information. We share it only as described below:</p>
        <ul>
          <li><strong>Service providers (processors):</strong> Supabase (database, auth, storage, edge functions), Resend (email delivery), Sentry (error monitoring), AI model providers accessed through the Lovable AI Gateway, and analytics tools. Each processes data on our behalf under contractual safeguards. Enterprise and agency customers may request our <a href="/dpa">Data Processing Agreement</a> for additional processor assurances.</li>
          <li><strong>Other users:</strong> content you choose to make public (e.g., public ideas, public profile fields, comments) is visible to other users and may be indexed by search engines.</li>
          <li><strong>Partners you connect with:</strong> if you submit a quote request, application, or "ask" to a partner, affiliate, or funder, we share the information you provide to fulfill that request.</li>
          <li><strong>Legal:</strong> when required by law, to enforce our agreements, to investigate fraud or abuse, or to protect rights, property, or safety.</li>
          <li><strong>Business transfers:</strong> in connection with a merger, acquisition, or asset sale, with notice to you.</li>
        </ul>
      </section>

      <section id="ai">
        <h2>6. AI Features</h2>
        <p>
          When you use the AI Coach, project grading, grant matching, or other AI features, the relevant prompt
          and project context are sent to third-party model providers through the Lovable AI Gateway to generate
          a response. We instruct providers not to train their general-purpose models on this content where
          such controls are available, but we cannot guarantee any provider's internal practices. Do not submit
          third-party trade secrets, regulated health information, or other content you are not authorized to
          share. Outputs may be inaccurate; verify before relying on them.
        </p>
      </section>

      <section id="retention">
        <h2>7. Retention</h2>
        <p>
          Headline windows: active accounts retained while in use; inactive accounts
          deleted after 36 months with prior notice; application logs 90 days; product
          analytics 14 months; database backups 30 days. When you delete your account you
          choose whether to keep public contributions (anonymized) or delete everything.
        </p>
        <p>
          See the full schedule, including auth/security data and legal holds, in our{" "}
          <a href="/retention">Records Retention & Deletion Policy</a>.
        </p>
      </section>

      <section id="security">
        <h2>8. Security</h2>
        <p>
          We use row-level security on our database, encryption in transit (TLS) and at rest, role-based access
          controls, and offer multi-factor authentication. We monitor errors and uptime continuously. No system
          is perfectly secure; you are responsible for keeping your password and any recovery codes safe.
        </p>
        <p>
          More detail on our controls, incident-response commitments, and how to report a
          vulnerability is published on our <a href="/security">Security page</a>.
        </p>
      </section>

      <section id="rights">
        <h2>9. Your Rights</h2>
        <p>Depending on where you live, you may have the right to access, correct, export, delete, restrict, or object to processing of your information, and to withdraw consent. You can:</p>
        <ul>
          <li>Update most profile and notification settings from <a href="/account">Account Settings</a>.</li>
          <li>Delete your account and associated data via Account Settings → Delete Account.</li>
          <li>Unsubscribe from non-transactional emails using the link in each message.</li>
          <li>Email <a href="mailto:info@emrkt.co">info@emrkt.co</a> for any request we cannot self-serve.</li>
        </ul>
        <p>
          For agency, funder, and partner customers, detailed processor obligations and data subject assistance terms are set out in our <a href="/dpa">Data Processing Agreement</a>.
        </p>
        <p>We do not discriminate against you for exercising these rights.</p>
      </section>

      <section id="children">
        <h2>10. Children</h2>
        <p>
          The Service is not directed to children under 13, and we do not knowingly collect personal information
          from them. If you believe a child has provided us information, contact us and we will delete it.
        </p>
      </section>

      <section id="international">
        <h2>11. International Users</h2>
        <p>
          The Service is operated from the United States. If you access it from outside the U.S., you understand
          your information will be transferred to and processed in the U.S. and other countries where our
          providers operate.
        </p>
      </section>

      <section id="changes">
        <h2>12. Changes</h2>
        <p>
          We may update this Policy from time to time. Material changes will be announced in-product or by email.
          The "Last updated" date above reflects the most recent revision.
        </p>
      </section>

      <section id="contact">
        <h2>13. Contact</h2>
        <p>
          The Emerging Market<br />
          Email: <a href="mailto:info@emrkt.co">info@emrkt.co</a><br />
          Mobile, Alabama, USA
        </p>
      </section>
    </LegalLayout>
  </>
);

export default PrivacyPolicy;